import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TextInput, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import InlineMessage from './InlineMessage';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

/** ResetPasswordModal - modal form for resetting another user's password with validation. */
interface ResetPasswordModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (password: string) => Promise<void>;
  readonly targetName: string;
}

export default function ResetPasswordModal({ visible, onClose, onSubmit, targetName }: ResetPasswordModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const hasLength = password.length >= 8;
  const passwordsMatch = password === confirm && password.length > 0;
  const isValid = hasUpper && hasLower && hasNumber && hasSpecial && hasLength && passwordsMatch;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await onSubmit(password);
      setPassword('');
      setConfirm('');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setErrorMsg(message);
    } finally { setSaving(false); }
  };

  const handleClose = () => {
    setPassword('');
    setConfirm('');
    setErrorMsg(null);
    onClose();
  };

  /** Check - renders a single password requirement row with check/circle icon. */
  const Check = ({ ok, label }: { readonly ok: boolean; readonly label: string }) => (
    <View style={styles.checkRow}>
      <Ionicons name={ok ? 'checkmark-circle' : 'ellipse-outline'} size={16} color={ok ? '#059669' : colors.textLight} />
      <Text style={[styles.checkText, ok && { color: '#059669' }]}>{label}</Text>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <View style={[styles.content, { marginBottom: modalBottomPadding }]}>
          <Text style={styles.title}>Reset Password</Text>
          <Text style={styles.subtitle}>Set a new password for {targetName}</Text>

          <View style={styles.inputWrap}>
            <TextInput
              style={styles.input}
              placeholder="New password"
              placeholderTextColor={colors.textLight}
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              maxLength={128}
            />
            <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
              <Ionicons name={showPassword ? 'eye-off' : 'eye'} size={20} color={colors.textLight} />
            </TouchableOpacity>
          </View>

          <TextInput
            style={styles.input}
            placeholder="Confirm password"
            placeholderTextColor={colors.textLight}
            value={confirm}
            onChangeText={setConfirm}
            secureTextEntry={!showPassword}
            maxLength={128}
          />

          <View style={styles.checks}>
            <Check ok={hasLength} label="8+ characters" />
            <Check ok={hasUpper} label="Uppercase letter" />
            <Check ok={hasLower} label="Lowercase letter" />
            <Check ok={hasNumber} label="Number" />
            <Check ok={hasSpecial} label="Special character" />
            <Check ok={passwordsMatch} label="Passwords match" />
          </View>

          {errorMsg && (
            <InlineMessage type="error" message={errorMsg} visible={true} onDismiss={() => setErrorMsg(null)} />
          )}

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={handleClose}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.saveBtn, !isValid && { opacity: 0.5 }]} onPress={handleSubmit} disabled={!isValid || saving}>
              <Text style={styles.saveText}>{saving ? 'Saving...' : 'Reset Password'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  content: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 420 },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  inputWrap: { flexDirection: 'row', alignItems: 'center' },
  input: {
    flex: 1, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    fontSize: fontSize.sm, color: c.text, marginBottom: spacing.sm, borderWidth: 1, borderColor: c.border,
  },
  eyeBtn: { position: 'absolute', right: 10, top: 10 },
  checks: { marginBottom: spacing.md },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
  checkText: { fontSize: fontSize.xs, color: c.textLight },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
